import type {EntradasBluequest, EstadoDeDocumentos, ResultadoDeGeneracion} from '@cnc/contracts';
import {useQueryClient} from '@tanstack/react-query';
import {useState, type ReactNode} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useGenerarDocumentos} from '../api/documentos';
import {
  consultarSofr,
  useBancosDeFinanciador,
  useFinanciacionDeExportacion,
  useGuardarFinanciacion,
} from '../api/financiacion';
import {tr, type Texto} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_DOCUMENTOS} from './ayuda-general';
import {AYUDA_BLUEQUEST as A} from './ayuda-financiacion-bluequest';
import {NotaDeAyuda} from './nota-de-ayuda';

const campo = 'mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta';
const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50';

type Datos = {
  factura: string;
  fijacionEl: string;
  precioAuUsd: string;
  precioAgUsd: string;
  banco: string;
  lote: string;
  desembolsoEsperado: string;
  sofr: string;
};
const aTexto = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const numeroDe = (t: string) => (t.trim() === '' ? null : Number(t.replace(',', '.')));
// De que empresa sale cada plantilla (decision 0061): la propia, la general o ninguna.
const deQuien = (p: {empresa: string | null} | null) =>
  p === null
    ? tr('falta', 'missing')
    : p.empresa
      ? tr(`de ${p.empresa}`, `from ${p.empresa}`)
      : tr('el general', 'the general one');

// Documentos Bluequest de una exportacion (Daniel, 15/10/2026): los datos que
// da Daniel (factura NIMBLQ, precios y su fecha, banco, Lot ref, fecha de pago
// esperada, SOFR del NY Fed) quedan en la financiacion; las fechas del
// certificado de ensayo van con la generacion.
export function DocumentosBluequest({
  codigo,
  estado,
  onResultado,
}: {
  codigo: string;
  estado: NonNullable<EstadoDeDocumentos['bluequest']>;
  onResultado: (r: ResultadoDeGeneracion) => void;
}) {
  const financiacion = useFinanciacionDeExportacion(codigo);
  const fin = financiacion.data?.financiacion ?? null;
  const bancos = useBancosDeFinanciador(estado.financiador);
  const guardar = useGuardarFinanciacion(codigo);
  const cliente = useQueryClient();
  const generar = useGenerarDocumentos(codigo);
  const [editados, setEditados] = useState<Partial<Datos>>({});
  const [fechas, setFechas] = useState<EntradasBluequest>(estado.fechas);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);
  const [trayendo, setTrayendo] = useState(false);

  const base: Datos = {
    factura: aTexto(fin?.campos.factura),
    fijacionEl: aTexto(fin?.campos.fijacionEl),
    precioAuUsd: aTexto(fin?.campos.precioAuUsd),
    precioAgUsd: aTexto(fin?.campos.precioAgUsd),
    banco: aTexto(fin?.campos.banco),
    lote: aTexto(fin?.campos.lote),
    desembolsoEsperado: aTexto(fin?.campos.desembolsoEsperado),
    // En pantalla el SOFR va en porcentaje (3,66746).
    sofr: typeof fin?.campos.sofr === 'number' ? String(Math.round(fin.campos.sofr * 1e7) / 1e5) : '',
  };
  const datos: Datos = {...base, ...editados};
  const poner = (k: keyof Datos) => (v: string) => setEditados(e => ({...e, [k]: v}));
  const cambios = (Object.keys(editados) as Array<keyof Datos>).filter(k => editados[k] !== base[k]);

  const guardarDatos = async () => {
    const sofr = numeroDe(datos.sofr);
    const campos: Record<string, unknown> = {
      factura: datos.factura.trim() || null,
      fijacionEl: datos.fijacionEl || null,
      precioAuUsd: numeroDe(datos.precioAuUsd),
      precioAgUsd: numeroDe(datos.precioAgUsd),
      banco: datos.banco ? Number(datos.banco) : null,
      lote: datos.lote.trim() || null,
      desembolsoEsperado: datos.desembolsoEsperado || null,
      sofr: sofr === null ? null : sofr / 100,
    };
    await guardar.mutateAsync({version: fin?.version ?? null, campos});
    setEditados({});
    // Lo que falta para generar se vuelve a revisar con los datos nuevos.
    await cliente.invalidateQueries({queryKey: ['documentos', 'estado', codigo]});
  };

  const traerSofr = async () => {
    if (!datos.fijacionEl) {
      setMensaje({
        texto: tr('Primero escribe la fecha de los precios.', 'First enter the price date.'),
        error: true,
      });
      return;
    }
    setTrayendo(true);
    setMensaje(null);
    try {
      const r = await consultarSofr(datos.fijacionEl);
      poner('sofr')(String(Math.round(r.tasa * 1e7) / 1e5));
      setMensaje({
        texto: tr(
          `SOFR a 90 días del NY Fed publicado el ${r.fecha}: ${numero(r.tasa * 100, 5)} %.`,
          `NY Fed 90-day SOFR published on ${r.fecha}: ${numero(r.tasa * 100, 5)} %.`,
        ),
        error: false,
      });
    } catch (error) {
      setMensaje({
        texto: error instanceof ErrorDeApi ? error.message : tr('No se pudo traer el SOFR.', 'Could not get the SOFR.'),
        error: true,
      });
    } finally {
      setTrayendo(false);
    }
  };

  const generarDocumentos = async () => {
    setMensaje(null);
    try {
      if (cambios.length) await guardarDatos();
      onResultado(await generar.mutateAsync({paquete: 'bluequest', entradas: fechas}));
    } catch (error) {
      setMensaje({
        texto:
          error instanceof ErrorDeApi
            ? [error.message, ...(error.detalles ?? [])].join(' ')
            : tr('No se pudo generar.', 'Could not generate.'),
        error: true,
      });
    }
  };

  const ocupado = guardar.isPending || generar.isPending;
  return (
    <section
      className="mt-4 space-y-3 rounded-lg border border-dorado/60 bg-dorado-claro/40 p-4"
      aria-label={tr('Documentos Bluequest', 'Bluequest documents')}
      data-prueba="documentos-bluequest"
    >
      <div>
        <h3 className="text-sm font-medium">
          {tr('Documentos Bluequest', 'Bluequest documents')}
          <NotaDeAyuda titulo={tr('Documentos Bluequest', 'Bluequest documents')} texto={A.paquete} />
        </h3>
        <p className="text-xs text-apagado">
          {tr(
            'TC calculation, factura y packing list del vendedor (con certificados de ensayo, origen y peso), packing list para Bluequest con la fecha de entrada a bodega y certificado de ensayo del laboratorio. Lo demás sale de la exportación y de sus Big Bags.',
            "TC calculation, seller's invoice and packing list (with assay, origin and weight certificates), Bluequest packing list with the warehouse entry date and the laboratory assay certificate. The rest comes from the export and its Big Bags.",
          )}
        </p>
      </div>
      <fieldset disabled={ocupado} className="grid gap-3 sm:grid-cols-4">
        <Campo etiqueta={tr('Factura al financiador', 'Invoice to the financer')} ayuda={A.factura}>
          <input
            value={datos.factura}
            onChange={e => poner('factura')(e.target.value)}
            placeholder="NIMBLQ-58"
            className={campo}
          />
        </Campo>
        <Campo etiqueta={tr('Fecha de los precios', 'Price date')} ayuda={A.fecha}>
          <input
            type="date"
            value={datos.fijacionEl}
            onChange={e => poner('fijacionEl')(e.target.value)}
            className={campo}
          />
        </Campo>
        <Campo etiqueta={tr('Precio del oro (USD/oz)', 'Gold price (USD/oz)')} ayuda={A.precioOro}>
          <input
            type="number"
            step="0.01"
            min="0"
            value={datos.precioAuUsd}
            onChange={e => poner('precioAuUsd')(e.target.value)}
            className={campo}
          />
        </Campo>
        <Campo etiqueta={tr('Precio de la plata (USD/oz)', 'Silver price (USD/oz)')} ayuda={A.precioPlata}>
          <input
            type="number"
            step="0.001"
            min="0"
            value={datos.precioAgUsd}
            onChange={e => poner('precioAgUsd')(e.target.value)}
            className={campo}
          />
        </Campo>
        <Campo etiqueta={tr('Banco', 'Bank')} ayuda={A.banco}>
          <select value={datos.banco} onChange={e => poner('banco')(e.target.value)} className={campo}>
            <option value="">—</option>
            {(bancos.data?.bancos ?? [])
              .filter(b => b.activo || String(b.id) === datos.banco)
              .map(b => (
                <option key={b.id} value={b.id}>
                  {b.nombre}
                </option>
              ))}
          </select>
        </Campo>
        <Campo etiqueta="Lot ref" ayuda={A.lote}>
          <input value={datos.lote} onChange={e => poner('lote')(e.target.value)} className={campo} />
        </Campo>
        <Campo etiqueta={tr('Fecha de pago esperada', 'Expected payment date')} ayuda={A.pagoEsperado}>
          <input
            type="date"
            value={datos.desembolsoEsperado}
            onChange={e => poner('desembolsoEsperado')(e.target.value)}
            className={campo}
          />
        </Campo>
        <Campo etiqueta={tr('SOFR 3 meses (%)', 'SOFR 3 month (%)')} ayuda={A.sofr}>
          <div className="mt-1 flex gap-1">
            <input
              type="number"
              step="0.00001"
              min="0"
              value={datos.sofr}
              onChange={e => poner('sofr')(e.target.value)}
              className="w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta"
            />
            <button
              type="button"
              className={botonSuave}
              onClick={() => void traerSofr()}
              disabled={trayendo}
              title={tr(
                'Traer el SOFR del NY Fed con la fecha de los precios',
                'Get the SOFR from the NY Fed with the price date',
              )}
            >
              {trayendo ? '…' : tr('↻ NY Fed', '↻ NY Fed')}
            </button>
          </div>
        </Campo>
      </fieldset>
      <fieldset disabled={ocupado} className="grid gap-3 sm:grid-cols-5">
        <legend className="mb-1 text-xs font-medium text-apagado">
          {tr('Certificado de ensayo del laboratorio', 'Laboratory assay certificate')}
          <NotaDeAyuda texto={A.fechasEnsayo} />
        </legend>
        {(
          [
            ['recepcionDesde', tr('Recepción desde', 'Received from')],
            ['recepcionHasta', tr('Recepción hasta', 'Received to')],
            ['ensayoDesde', tr('Ensayo desde', 'Assayed from')],
            ['ensayoHasta', tr('Ensayo hasta', 'Assayed to')],
            ['emitido', tr('Emitido el', 'Issued on')],
          ] as Array<[keyof EntradasBluequest, string]>
        ).map(([k, etiqueta]) => (
          <label key={k} className="block text-xs text-apagado">
            {etiqueta}
            <input
              type="date"
              value={fechas[k] ?? ''}
              onChange={e => setFechas(f => ({...f, [k]: e.target.value || null}))}
              className={campo}
            />
          </label>
        ))}
      </fieldset>
      {estado.faltan.length > 0 && !cambios.length && (
        <div className="rounded-md border border-alerta/40 bg-white px-3 py-2 text-xs text-alerta" role="alert">
          {tr('Para generarlos falta:', 'To generate them this is missing:')} {estado.faltan.join('; ')}.
        </div>
      )}
      <p className="text-xs text-apagado" data-prueba="plantillas-bluequest">
        {tr('Logo:', 'Logo:')} {deQuien(estado.plantillas.logo)} · {tr('Firma:', 'Signature:')}{' '}
        {deQuien(estado.plantillas.firma)} · {tr('Certificado de ensayo:', 'Assay certificate:')}{' '}
        {deQuien(estado.plantillas.ensayo)}
        <NotaDeAyuda texto={AYUDA_DOCUMENTOS.deLaEmpresa} />
      </p>
      {estado.avisos.length > 0 && (
        <ul className="space-y-0.5 text-xs text-apagado">
          {estado.avisos.map(a => (
            <li key={a}>⚠ {a}</li>
          ))}
        </ul>
      )}
      {mensaje && (
        <p role="status" className={`text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`}>
          {mensaje.texto}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={botonSuave}
          disabled={ocupado || !cambios.length}
          onClick={() => void guardarDatos()}
        >
          {guardar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar datos', 'Save data')}
        </button>
        <button type="button" className={boton} disabled={ocupado} onClick={() => void generarDocumentos()}>
          {generar.isPending
            ? tr('Generando…', 'Generating…')
            : tr('↓ Generar documentos Bluequest', '↓ Generate Bluequest documents')}
        </button>
      </div>
    </section>
  );
}

function Campo({etiqueta, ayuda, children}: {etiqueta: string; ayuda: Texto; children: ReactNode}) {
  return (
    <label className="block text-xs text-apagado">
      {etiqueta}
      <NotaDeAyuda titulo={etiqueta} texto={ayuda} dentroDeEtiqueta />
      {children}
    </label>
  );
}
